import { A } from '#app/components/typography/a'
import { H1 } from '#app/components/typography/h1.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { H3 } from '#app/components/typography/h3.tsx'
import { H4 } from '#app/components/typography/h4.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Ul } from '#app/components/typography/ul'
import { Code } from '#app/old_components/typography/code.tsx'
import { Kbd } from '#app/old_components/typography/kbd.tsx'
import { CodeBlock } from '#app/old_components/ui/code-block.tsx'

export const handle = {
	breadcrumb: 'Introduction',
}

export default function IntroductionRoute() {
	return (
		<div>
			<H1>Introduction</H1>
			<P className="text-lg">Copy and paste-able components built with React Aria and Tailwind CSS, written in TypeScript.</P>
			<P>VerveUI has a short learning curve, but you do need to understand the following concepts first.</P>

			<H2>Design System</H2>
			<P>VerveUI has a fully customizable design system. The design system is divided into two parts: design tokens and component styles.</P>

			<H3>Design tokens</H3>
			<P>
				Design tokens are design decisions written in CSS variables in the <Code>design-system.css</Code> file. They provide a source of truth for the design and ensure it is consistent
				across the entire UI. There are the following design tokens:
			</P>
			<Ul>
				<li>Space tokens</li>
				<li>Color tokens</li>
				<li>Font family tokens</li>
				<li>Font size tokens</li>
				<li>Font line-height tokens</li>
				<li>Font tracking tokens</li>
				<li>Roundness tokens</li>
				<li>Shadow tokens</li>
				<li>Border tokens</li>
			</Ul>
			<P>
				On its own, this file does nothing except declare CSS variables. These CSS variables will be used by the new utility classes we extend Tailwind with to inform the component
				styles.
			</P>

			<H4>Extended Tailwind Utility Classes</H4>
			<P>
				The variables in <Code>design-system.css</Code> will be used by the <Code>extended-theme.ts</Code> file to extend the default Tailwind utility classes. We don't change the
				default Tailwind classes to make sure developers have no surprises. Instead we create a few new utility classes described after the component styles.
			</P>

			<H3>Component styles</H3>
			<P>
				The styles for the components are defined in the <Code>component-styles.css</Code> file. This file holds the component classes (and their variants) and defines the styles for
				each component.
			</P>

			<P>
				The <Code>design-system.css</Code>, <Code>extended-theme.ts</Code>, and <Code>component-styles.css</Code> files are the only files you need to edit to customize the design and
				can be thought of as owned by the designer. Unless you're adding/removing components or variants, a design change = a commit to these files.
			</P>

			<H2>Fluid space scale</H2>
			<P className="text-lg">
				Tailwind already has a{' '}
				<A href="https://tailwindcss.com/docs/customizing-spacing#default-spacing-scale" target="_blank">
					spacing scale
				</A>
				. This spacing scale has a few problems, described below. The <Code>{'*-space-{size}'}</Code> utility is the solution to these problems with 21 steps, from 0 to 20.
			</P>
			<P>
				Spacing is the cement that holds together the bricks of components on a UI. The importance of good spacing is hard to overstate: it's the most important aspect of design. And the
				difference between an exquisitely elegant design and a graceless one. So it's no wonder designers are rightly taught to pay great attention to whitespace.
			</P>
			<P>
				However, it remains one of the most unnecessarily unsystematized aspects of web design (and by consequence development). Most designers still space things out by hand:{' '}
				<Kbd>Shift + Left</Kbd>, <Kbd>Shift + Right</Kbd>, <Kbd>Shift + Up</Kbd>, and <Kbd>Shift + Down</Kbd> to move things around by increments of 10px and counting 10, 20, 30 in their
				heads is the bread and butter of spacing.
			</P>
			<P>
				As for developers, spacing is handled with hard-coded utility classes, infinitely growing BEM classes, or, God forbid, CSS in style tags. Out of all of these, utility classes is
				the least worse: if there's a clear spacing scale agreed upon between developer and designer beforehand, it's relatively easy to implement a given design. But if the designer
				decides to change a spacing rule (from <Code>w-4</Code> to <Code>w-5</Code>, for example) after the implementation is done, you're going to have a hard time figuring out which
				instances of that <Code>w-4</Code> pertain to the spacing the designer wants to change and which should remain the same.
			</P>
			<P>We can do better.</P>
			<P>
				Let's give spacing the well-thought-out treatment it deserves and enable clear communication between designer and developer, preserve the designer's complete control over
				spacing, even after the implementation is complete, and greatly speed up development in the process.
			</P>

			<H3>Modular scale</H3>
			<P>
				One popular method for establishing a scale is to use a modular scale, based on a specific ratio. Ratios like 4:5, 2:3, or the "golden ratio" of 1:1.618 are commonly used.
				Typically, you start with a base size, often 16px (a common default for browsers), and then apply your chosen ratio to calculate the subsequent sizes in your scale.
			</P>
			<P>
				While the mathematical elegance of this approach is appealing, it has a deal-breaking limitation: limited size options. You could try different ratios and equations, but at that
				point you’re just trying to pick a scale that happens to match the sizes you already know you want.
			</P>
			<H3>Hand-picked scale</H3>
			<P>It's more pragmatic to hand-pick the values because this approach grants the designer complete control over the number and variety of space sizes.</P>
			<P>Let's take a look at TailwindCSS's spacing scale.</P>
			<div className="ring-opacity-5 mt-8 overflow-hidden ring-1 shadow ring-black sm:rounded-lg">
				<table className="divide-muted-300 min-w-full divide-y">
					<thead className="bg-muted-50">
						<tr>
							<th scope="col" className="text-muted-900 py-3.5 pr-3 pl-4 text-left text-sm font-semibold sm:pl-6">
								Name
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Size
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Pixels
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Visual representation
							</th>
						</tr>
					</thead>
					<tbody className="divide-muted-200 divide-y bg-white">
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">0</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm"></td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-px" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">0.5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.125rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[2px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">1</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[4px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">1.5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.375rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[6px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">2</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[8px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">2.5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.625rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[10px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">3</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[12px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">3.5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.875rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[14px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">4</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[16px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[20px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">6</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[24px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">7</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">28px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[28px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">8</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">32px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[32px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">9</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">36px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[36px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">10</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">40px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[40px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">11</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">44px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[44px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">12</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">3rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">48px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[48px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">14</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">3.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">56px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[56px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">16</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">64px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[64px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">20</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">80px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[80px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">24</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">96px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[96px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">28</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">7rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">112px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[112px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">32</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">128px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[128px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">36</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">9rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">144px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[144px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">40</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">160px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[160px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">44</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">11rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">176px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[176px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">48</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">192px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[192px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">52</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">13rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">208px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[208px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">56</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">224px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[224px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">60</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">15rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">240px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[240px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">64</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">256px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[256px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">72</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">18rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">288px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[288px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">80</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">320px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[320px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">96</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">384px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[384px]" />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<P>This is a comprehensive enough scale, however it does have a few problems.</P>
			<P>
				First, there's one major no-go: semantics are tied with implementation. The names of the steps in the scale (1, 1.5, 2, 2.5, etc) are tied to multiples of 4px, which is an
				implementation detail. In order for the designer to have complete control over the design after the implementation is done, semantics and implementation have to be decoupled.
				This is absolutely crucial for development speed.
			</P>
			<P>
				The other awkward thing is that we have half steps (0.5, 1.5, 2.5, etc) on the lower end of the scale because we need multiples of 2px, and we skip steps (64, 72, 80, etc) on the
				higher end of the scale because we don't need those multiples of 4px. This inconsistency makes for a harder-to-communicate scale and an awkward developer experience where if the
				designer asks you to step a component's spacing up, you don't go from 64 to 65, but from 64 to 72.
			</P>
			<P>Let's do better.</P>
			<P>
				Instead of having a scale whose steps are tied to multiples of 4px, let's create a scale that just has "steps" going up. Tailwind has 35 steps, from 0 to 34, so let's replicate
				that.
			</P>
			<div className="ring-opacity-5 mt-8 overflow-hidden ring-1 shadow ring-black sm:rounded-lg">
				<table className="divide-muted-300 min-w-full divide-y">
					<thead className="bg-muted-50">
						<tr>
							<th scope="col" className="text-muted-900 py-3.5 pr-3 pl-4 text-left text-sm font-semibold sm:pl-6">
								Name
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Size
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Pixels
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Visual representation
							</th>
						</tr>
					</thead>
					<tbody className="divide-muted-200 divide-y bg-white">
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">0</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm"></td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">1</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[1px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">2</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.125rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[2px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">3</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[4px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">4</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.375rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[6px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[8px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">6</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.625rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[10px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">7</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[12px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">8</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0.875rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[14px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">9</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[16px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">10</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[20px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">11</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[24px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">12</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">28px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[28px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">13</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">32px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[32px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">14</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.25rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">36px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[36px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">15</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">40px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[40px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">16</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2.75rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">44px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[44px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">17</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">3rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">48px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[48px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">18</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">3.5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">56px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[56px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">19</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">64px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[64px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">20</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">5rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">80px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[80px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">21</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">96px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[96px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">22</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">7rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">112px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[112px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">23</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">128px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[128px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">24</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">9rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">144px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[144px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">25</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">160px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[160px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">26</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">11rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">176px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[176px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">27</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">192px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[192px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">28</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">13rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">208px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[208px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">29</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">224px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[224px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">30</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">15rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">240px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[240px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">31</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">256px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[256px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">32</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">18rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">288px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[288px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">33</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">320px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[320px]" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">34</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24rem</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">384px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-[384px]" />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<P>This simple change is deceptively powerful.</P>
			<P>
				First, the designer doesn't have to think about absolute values (4px, 10px, 16px, etc), but relative ones ("this should be more spaced out than that by 2 steps", "that should be
				more spaced out than this by 4 steps", etc). Eventually the designer has to converge on absolute values, of course, but as long as the designer starts with a somewhat decent
				scale, the flexibility to think in relative terms is a huge speed booster, specially in the early phases of design.
			</P>
			<P>
				Also, if the designer gets the relative values right - which is much easier than to get the absolute values right - the implementation will not change: step 30 will still be step
				30, whether the designer decides later that that means 240px or 244px. This frees the developer to develop at full speed and, given an initial mockup, <b>in parallel</b>, without
				having to wait for the designer to get the values right.
			</P>
			<P>
				Finally, by giving the designer complete control over the absolute values, we eliminate an entire category of back-and-forth work between designer and developer! The only
				possible mistakes or changes that require coordination between the two are those that involve creating or eliminating steps. But this is a much smaller set of changes.
			</P>

			<H3>Breakpoint-based sizing</H3>
			<P>
				We still need to design and develop for multiple screen sizes. This is usually done by having designers design mockups for multiple sizes to account for phones, tablets, and
				desktops. It's not uncommon for a designer to design mockups for 4 or 5 different screen sizes. Then, the developer has to go through the entire codebase and add rules for each
				breakpoint. Tailwind{' '}
				<A href="https://tailwindcss.com/docs/responsive-design" target="_blank" rel="noreferrer">
					has 5 breakpoints
				</A>
				, so at it's most responsive, there's 5 breakpoints per component. If you have 100 components, that's 500 breakpoints.
			</P>
			<P>Doing all this is a tremendous waste of effort.</P>
			<P>Let's do better.</P>

			<H3>Fluid sizing</H3>
			<P>
				The{' '}
				<A href="https://developer.mozilla.org/en-US/docs/Web/CSS/clamp" target="_blank" rel="noreferrer">
					<Code>clamp()</Code>
				</A>{' '}
				function , introduced in CSS, offers a fluid approach to sizing. It allows designers to define a minimum and maximum size range, ensuring a smooth scale between these values
				based on the viewport size.
			</P>
			<P>
				For example, you can use the <Code>clamp(16px, 5vw, 24px)</Code> to set a size that is 5% of the viewport width, 16 pixels on small screens, and a maximum of 24 pixels on larger
				screens. This results in a seamless transition of sizes as the screen size changes, also providing a smoother experience for users.
			</P>
			<P>
				However, the <Code>vw</Code> unit isn't ideal: the size can start to grow too soon, too late, too slow, or too fast. What we want is a linear growth rate from the minimum
				viewport size, 320px say, to the maximum width the website will have, 1536px say. Imagine the size on the y axis and the viewport width on the x axis. Here's how we can use CSS
				variables and the calc() function to calculate the exact growth rate we want between those two points, using the general equation of a straight line: y = mx + c.
			</P>
			<P>This is how we implement this linear growth rate in VerveUI.</P>
			<CodeBlock
				className="mt-6"
				filename="design-tokens"
				extension="css"
				code={`@layer base {
	:root {
		--content-min-width: 20; /* in rems = 320px */
		--content-max-width: 96; /* in rems = 1536px */

		--scale-20-min-size: 10; /* in rems = 160px */
		--scale-20-max-size: 26; /* in rems = 416px */

		/* space-20 */
		--size-deltaX: calc(var(--content-max-width) - var(--content-min-width));
		--space-20-deltaY: calc(var(--scale-20-max-size) - var(--scale-20-min-size));
		--space-20-gradient: calc(var(--space-20-deltaY) / var(--size-deltaX));
		--space-20-intercept: calc(var(--scale-20-min-size) - (var(--space-20-gradient) * var(--content-min-width)));
		--space-20-font-size: calc(var(--space-20-gradient) * 100vw + var(--space-20-intercept) * 1rem);
		--space-20: clamp(calc(var(--scale-20-min-size) * 1rem), var(--space-20-font-size), calc(var(--scale-20-max-size) * 1rem));
	}
}`}
			/>
			<P>
				The design process to implement fluid sizing is rather simple for both designers and developers. Designers only have to draw two mockups: one for small screens and one for large
				screens. While the developer only needs to tell the browser to interpolate between the two scales, based on the current viewport width.
			</P>
			<P>
				Additionally, if we're going to use fluid sizing instead of breakpoint-based sizing, we don't need as many steps on the scale, because a space of 384px would always be too
				overwhelming (it would overflow, in fact), on a 320px wide viewport.
			</P>
			<P>
				And we need no variation in the lower steps, some variation from step 15 onwards, and more variation in the higher steps because of the two opposing forces between the need to
				increase the spacing on larger screens to keep the design balanced, and the fact that users don't resize their browser windows to see the same content bigger, but to see more
				content - and therefore we don't want to just scale up the entire UI (that'd be a terrible UX).
			</P>
			<P>So let's cap the scale at step 20 and add a min and a max space size.</P>
			<div className="ring-opacity-5 mt-8 overflow-hidden ring-1 shadow ring-black sm:rounded-lg">
				<table className="divide-muted-300 min-w-full divide-y">
					<thead className="bg-muted-50">
						<tr>
							<th scope="col" className="text-muted-900 py-3.5 pr-3 pl-4 text-left text-sm font-semibold sm:pl-6">
								Name
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Pixels (min)
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Pixels (max)
							</th>
							<th scope="col" className="text-muted-900 py-3.5 px-3 text-left text-sm font-semibold">
								Visual representation
							</th>
						</tr>
					</thead>
					<tbody className="divide-muted-200 divide-y bg-white">
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">0</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">0px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-0" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">1</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">1px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-1" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">2</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">2px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-2" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">3</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">4px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-3" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">4</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">6px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-4" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">5</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">8px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-5" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">6</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">10px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">7</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">12px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-7" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">8</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">14px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-8" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">9</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">16px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-9" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">10</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">20px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-10" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">11</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">24px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-11" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">12</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">28px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">28px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-12" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">13</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">32px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">32px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="w-13 bg-brand-bg h-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">14</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">36px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">36px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-14" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">15</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">40px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">48px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="w-15 bg-brand-bg h-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">16</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">48px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">64px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-16" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">17</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">64px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">96px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="w-17 bg-brand-bg h-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">18</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">80px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">144px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="w-18 bg-brand-bg h-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">19</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">128px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">256px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="w-19 bg-brand-bg h-6" />
							</td>
						</tr>
						<tr>
							<td className="text-muted-900 whitespace-nowrap py-4 pr-3 pl-4 text-sm font-medium sm:pl-6">20</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">160px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">416px</td>
							<td className="text-muted-500 whitespace-nowrap py-4 px-3 text-sm">
								<div className="bg-brand-bg h-6 w-20" />
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<P>Resize your browser window to see how the bars change size starting from step 15 onwards.</P>

			<H3>Note on inversion of control</H3>
			<P>
				The technique we're using here is generally referred to, in software engineering, as "inversion of control". We create an abstraction (the scale) and implement our UI using it.
				This allows the designer to retain complete control over the specific values each step in the scale maps to.
			</P>

			<H3>The {'*-space-{size}'} utility</H3>
			<P>
				VerveUI comes with a <Code>{'*-space-{size}'}</Code> utility that allows you to use this fluid scale in your UI. For example, <Code>{'w-space-20'}</Code> will give you a width of{' '}
				<Code>160px</Code> on <Code>320px</Code> screens, a width of <Code>416px</Code> on <Code>1536px</Code> screens, and a dinamically calculated width on all other screen sizes.
			</P>
			<P>
				The <Code>{'*-space-{size}'}</Code> utility works with all the properties that accept a size value, such as <Code>padding</Code>, <Code>margin</Code>, <Code>width</Code>,{' '}
				<Code>minWidth</Code>, <Code>maxWidth</Code>, <Code>height</Code>, <Code>minHeight</Code>, <Code>maxHeight</Code>, <Code>gap</Code>, <Code>inset</Code>, <Code>space</Code>,{' '}
				<Code>translate</Code>, <Code>scrollMargin</Code>, and <Code>scrollPadding</Code>.
			</P>

			<H2>Fluid typography scale</H2>
			<P>
				Just like the fluid space scale and utility, we also have a fluid typography scale and utility. Because we don't need as many values, the scale is based on t-shirt sizes:{' '}
				<Code>text-size-6xl</Code>, <Code>text-size-5xl</Code>, <Code>text-size-4xl</Code>, <Code>text-size-3xl</Code>, <Code>text-size-2xl</Code>, <Code>text-size-xl</Code>,{' '}
				<Code>text-size-lg</Code>, <Code>text-size-md</Code>, <Code>text-size-sm</Code>, and <Code>text-size-xs</Code>.
			</P>
			<P>
				Just like the fluid space scale, the text size dynamically adapts to the viewport width, just like the default <Code>{'text-{size}'}</Code> utility, it comes with line-height
				and, additionally it also comes with tracking.
			</P>
			<CodeBlock
				className="mt-6"
				filename="fluid-typography"
				extension="css"
				code={`.text-size-6xl {
   font-size: var(--text-size-6xl);
   line-height: var(--text-line-height-6xl);
   letter-spacing: var(--text-tracking-6xl);
}`}
			/>

			<H3>Note on fluid design & development</H3>
			<P>
				Although fluid (as opposed to breakpoint-based) design & development is conceptually simple, it does come with powerful benefits. Once you've experienced not having to think
				about breakpoints or screen sizes, building components that automatically adapt to the space they have, with minimal and elegant code, and witness the visual harmony and
				consistency it gives you, you'll never go back.
			</P>

			<P>VerveUI follows these ideals:</P>
			<Ul>
				<li>These components should be able to be used in any framework that supports React.</li>
				<li>Copy and paste components give you full control over the component code, it is yours to use.</li>
				<li>Simple and beautiful default design.</li>
				<li>
					Design code and decisions are decoupled from development code and decisions: design change = <Code>tailwind.css</Code> commit.
				</li>
				<li>
					<Code>tailwind.css</Code> contains a full design system with{' '}
					<a href="https://andrecasal.com/articles/fluid-space-sizing" target="_blank" rel="noreferrer" className="underline">
						fluid space sizing
					</a>{' '}
					and{' '}
					<a href="https://andrecasal.com/articles/fluid-typography-sizing-with-modular-scales" target="_blank" rel="noreferrer" className="underline">
						fluid typography sizing and modular scales
					</a>
					.
				</li>
			</Ul>
			<P>That's it about the design system. I hope you enjoy using VerveUI.</P>
		</div>
	)
}
